import { INTERVIEW_STAGES, plural, WAIT_STAGES } from '@jt/core';
import type { ApplicationListItem, Task, Today } from '@jt/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppActions } from '../api/hooks';
import { useOpenDraft } from '../drafts/useDraft';
import { useUI } from './overlays';
import { Icon, type IconName } from './ui';

export type KpiKey = 'active' | 'waiting' | 'interviews' | 'offers' | 'rejected';

export const KPI_TITLES: Record<KpiKey, string> = {
  active: 'Отклики',
  waiting: 'Ждут ответа',
  interviews: 'Интервью',
  offers: 'Офферы',
  rejected: 'Отказы',
};

export const isKpiKey = (v: string | null): v is KpiKey => !!v && v in KPI_TITLES;

/** Та же выборка, что и счётчик (kpis() в @jt/core), — по данным списка вакансий. */
export function kpiMatch(k: KpiKey, a: Pick<ApplicationListItem, 'stage' | 'needsReply'>): boolean {
  switch (k) {
    case 'active':
      // все отправленные отклики, включая отказы; «Интересно» — ещё не отклик
      return a.stage !== 'saved';
    case 'waiting':
      return a.stage === 'applied' || (WAIT_STAGES.includes(a.stage) && !a.needsReply);
    case 'interviews':
      return INTERVIEW_STAGES.includes(a.stage);
    case 'offers':
      return a.stage === 'offer';
    case 'rejected':
      return a.stage === 'rejected';
  }
}

/** Счётчики — ссылки: по клику «Мои отклики» показывают только эти карточки. */
export function Kpis({ today, current = null }: { today: Today | undefined; current?: KpiKey | null }) {
  const k = today?.kpis;
  const items: [KpiKey, IconName, number | undefined, string][] = [
    ['active', 'send', k?.active, 'b'],
    ['waiting', 'clock', k?.waiting, 'o'],
    ['interviews', 'calendar', k?.interviews, 'v'],
    ['offers', 'trophy', k?.offers, 'g'],
    ['rejected', 'x', k?.rejected, 'r'],
  ];
  return (
    <div className="kpis">
      {items.map(([key, ic, n, c]) => (
        <Link
          className={`kpi ${current === key ? 'on' : ''}`}
          key={key}
          to={current === key ? '/applications' : `/applications?f=${key}`}
          aria-current={current === key ? 'true' : undefined}
        >
          <span className={`kic ${c}`}>
            <Icon name={ic} size={24} />
          </span>
          <div>
            <span>{KPI_TITLES[key]}</span>
            <b>{n ?? '—'}</b>
          </div>
        </Link>
      ))}
    </div>
  );
}

function TaskRow({ t }: { t: Task }) {
  const ui = useUI();
  const act = useAppActions();
  const nav = useNavigate();
  const openDraft = useOpenDraft();
  const a = t.action;
  const onAction = () => {
    if (a.kind === 'nav') nav('/offers');
    else if (a.kind === 'draft') openDraft(t.applicationId, a.draft);
    else if (a.kind === 'done')
      act.stepDone(t.applicationId).then(
        () => ui.toast('Отмечено. Назначьте следующий шаг, когда он появится.'),
        () => ui.toast('Не сохранилось. Повторите.'),
      );
    else if (a.kind === 'ghost')
      act.stage(t.applicationId, 'ghosted').then(
        () => ui.toast('Перенесено в «Нет ответа»'),
        () => ui.toast('Не сохранилось. Повторите.'),
      );
    else ui.openApp(t.applicationId);
  };
  return (
    <div className="trow">
      <span className={`tic ${t.tone}`}>
        <Icon name={t.icon} size={20} />
      </span>
      <button className="tmain" onClick={() => ui.openApp(t.applicationId)}>
        <b>{t.title}</b>
        <span className={t.late ? 'late' : ''}>{t.sub}</span>
      </button>
      <button className="btn sm" onClick={onAction}>
        {a.label}
      </button>
      <button className="icon-btn" onClick={() => ui.openApp(t.applicationId)} aria-label="Открыть карточку">
        <Icon name="chevR" />
      </button>
    </div>
  );
}

export function TodayBlock({ today }: { today: Today | undefined }) {
  const [all, setAll] = useState(false);
  const nav = useNavigate();
  if (!today) return null;
  const rows = [
    ...(today.pendingInbox
      ? [
          <div className="trow" key="inbox">
            <span className="tic v">
              <Icon name="mail" size={20} />
            </span>
            <button className="tmain" onClick={() => nav('/messages')}>
              <b>Письма к разбору · {today.pendingInbox}</b>
              <span>Claude нашёл события по откликам в Gmail</span>
            </button>
            <button className="btn sm" onClick={() => nav('/messages')}>
              Разобрать
            </button>
            <button className="icon-btn" onClick={() => nav('/messages')} aria-label="Открыть сообщения">
              <Icon name="chevR" />
            </button>
          </div>,
        ]
      : []),
    ...today.tasks.map((t) => <TaskRow key={`${t.applicationId}-${t.icon}`} t={t} />),
  ];
  if (!rows.length)
    return (
      <section className="today">
        <div className="th">
          <h2>Сегодня</h2>
          <span>срочных дел нет</span>
        </div>
      </section>
    );
  return (
    <section className="today">
      <div className="th">
        <h2>Сегодня</h2>
        <span>{plural(rows.length, 'задача', 'задачи', 'задач')}</span>
      </div>
      {all ? rows : rows.slice(0, 3)}
      {rows.length > 3 && (
        <button className="more-link" onClick={() => setAll(!all)}>
          {all ? 'Свернуть' : 'Показать все'}
        </button>
      )}
    </section>
  );
}
